import { NgModule } from "@angular/core";
import { CommonModule, DatePipe } from "@angular/common";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { RouterModule } from "@angular/router";
import { HttpClientModule, HttpClientJsonpModule } from "@angular/common/http";
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';

import { NgPipesModule } from 'ngx-pipes';
import { ClipboardModule } from 'ngx-clipboard';
import { NgIdleKeepaliveModule } from '@ng-idle/keepalive';
import { ToastrModule } from 'ngx-toastr';

import { DropOpenDirective } from './directives/menuopen.directive';
import { AutofocusDirective } from './directives/autofocus.directive';
import { DatePickerClearDirective } from "./directives/date-picker-clear-directive";
import { RunScriptsDirective } from "./directives/runscripts-directive";
import { HighlightDirective } from './directives/search-highlight-directive';
import { AfterValueChangedDirective } from './directives/after-value-changed-directive';
import { NgxPrintDirective } from './directives/ngx-print.directive';
import { ScrollTrackerDirective } from "./directives/scroll-tracker.directive";
import { FileDragNDropDirective } from "./directives/file-drag-n-drop.directive";
import { PositiveNumbersOnlyDirective } from './directives/positive-numbers-only.directive';

import { keepHtmlPipe } from "./pipes/keepHtml.pipe";
import { escapeHtmlPipe } from "./pipes/safe-html.pipes";
import { decodeHtmlPipe } from "./pipes/decodeHtml.pipe";
import { CustomSlice } from "./pipes/CustomSlice.pipes";

import { PageNotFoundComponent } from './components/page-not-found/page-not-found.component';
import { ServerErrorComponent } from './components/server-error/server-error.component';
import { ForbiddenComponent } from './components/forbidden/forbidden.component';
import { TermsConditionsComponent } from './components/terms-conditions/terms-conditions.component';
import { PrivacyPolicyComponent } from './components/privacy-policy/privacy-policy.component';
import { AppHelpComponent } from './components/app-help/app-help.component';
import { ContactComponent } from './components/contact/contact.component';
import { PortalFeatureContentComponent } from './components/portal-feature-content/portal-feature-content.component';
import { DocumentViewerComponent } from './components/document-viewer/document-viewer.component';
import { SubmitQuestionComponent } from './components/submit-question/submit-question.component';
import { EpaSignatureComponent } from './components/epa-signature/epa-signature.component';
import { EpaBulkSignatureComponent } from './components/epa-bulk-signature/epa-bulk-signature.component';
import { SafetyreportsSignaturelistComponent } from './components/safetyreports-signaturelist/safetyreports-signaturelist.component';
import { PlaceInRegdocComponent } from './components/place-in-regdoc/place-in-regdoc.component';
import { TopNotificationComponent } from "./components/top-notification/top-notification.component";
import { FeedbackBotComponent } from './components/feedback-bot/feedback-bot.component';
import { CheckboxComponent } from './components/report-filters/checkbox/checkbox.component';
import { DateComponent } from './components/report-filters/date/date.component';
import { DaterangeComponent } from './components/report-filters/daterange/daterange.component';
import { DropdownComponent } from './components/report-filters/dropdown/dropdown.component';
import { GridviewComponent } from './components/report-filters/gridview/gridview.component';
import { TextboxComponent } from './components/report-filters/textbox/textbox.component';
import { MatSelectSearchComponent } from './components/mat-select-search/mat-select-search.component';
import { KendoEditorComponent } from "./components/kendo-editor/kendo-editor.component";
import { MedptToastComponent } from "./components/medpt-toast/medpt-toast.component";
import { AppsearchComponent } from "./components/appsearch/appsearch.component";
import { DelegatedUserInfoComponent } from './components/delegated-user-info/delegated-user-info.component';
import { RaspberryAIComponent } from './components/raspberry-ai/raspberry-ai.component';
import { CitationPopoverComponent } from './components/raspberry-ai/citation-popover/citation-popover.component';
import { DocumentViewerAiChatComponent } from './components/document-viewer-ai-chat/document-viewer-ai-chat.component';
import { DocumentViewerAiInsightsComponent } from './components/document-viewer-ai-insights/document-viewer-ai-insights.component';


import { FullCalendarModule } from '@fullcalendar/angular';
import { DynamicFormBuilderModule } from './components/dynamic-form-builder/dynamic-form-builder.module';
import { MaterialModule } from "./material.module";
import { KendoModule } from "./kendo.module";
import { MedptEventLogDirective } from "./directives/medpt-common-event-log";
import { NgxDaterangepickerMd } from 'ngx-daterangepicker-material';
import { DelegateTrainingOthersComponent } from "./components/delegate-training-others/delegate-training-others.component";
import { ColonSeperatedFilterComponent } from "./components/colon-seperated-filter/colon-seperated-filter.component";
import { AdminBreadcrumbComponent } from './components/admin-breadcrumb/admin-breadcrumb.component';

const components = [
  PageNotFoundComponent,
  ServerErrorComponent,
  ForbiddenComponent,
  TermsConditionsComponent,
  PrivacyPolicyComponent,
  AppHelpComponent,
  ContactComponent,
  PortalFeatureContentComponent,
  DocumentViewerComponent,
  DocumentViewerAiChatComponent,
  DocumentViewerAiInsightsComponent,
  SubmitQuestionComponent,
  EpaSignatureComponent,
  EpaBulkSignatureComponent,
  PlaceInRegdocComponent,
  TopNotificationComponent,
  FeedbackBotComponent,
  RaspberryAIComponent,
  CitationPopoverComponent,
  SafetyreportsSignaturelistComponent,
  FeedbackBotComponent,
  MatSelectSearchComponent,
  CheckboxComponent,
  DateComponent,
  DaterangeComponent,
  DropdownComponent,
  GridviewComponent,
  TextboxComponent,
  AppsearchComponent,
  KendoEditorComponent,
  MedptToastComponent,
  DelegatedUserInfoComponent,
  DelegateTrainingOthersComponent,
  ColonSeperatedFilterComponent,
  AdminBreadcrumbComponent
];

const pipes = [
  keepHtmlPipe,
  CustomSlice,
  escapeHtmlPipe,
  decodeHtmlPipe
];

const directives = [
  MedptEventLogDirective,
  DropOpenDirective,
  DatePickerClearDirective,
  NgxPrintDirective,
  RunScriptsDirective,
  AutofocusDirective,
  HighlightDirective,
  PositiveNumbersOnlyDirective,
  AfterValueChangedDirective,
  ScrollTrackerDirective,
  FileDragNDropDirective
];

const modules = [
  NgbModule,
  CommonModule,
  FormsModule,
  ReactiveFormsModule,
  HttpClientModule,
  HttpClientJsonpModule,
  RouterModule,
  DynamicFormBuilderModule,
  NgPipesModule,
  ClipboardModule,
  FullCalendarModule,
  KendoModule,
  MaterialModule,
  NgIdleKeepaliveModule,
  ToastrModule
];

@NgModule({
  declarations: [
    ...directives,
    ...components,
    ...pipes
  ],
  imports: [
    ...modules,
    NgIdleKeepaliveModule.forRoot(),
    ToastrModule.forRoot({ toastComponent: MedptToastComponent, preventDuplicates: true, timeOut: 8000, autoDismiss: false, enableHtml: true, includeTitleDuplicates: true, newestOnTop: true, tapToDismiss: false, closeButton: false }),
    NgxDaterangepickerMd.forRoot(),
  ],
  exports: [
    ...modules,
    ...directives,
    ...components,
    ...pipes,
    DatePipe
  ],
  providers: [
    ...pipes,
    DatePipe
  ]
})
export class SharedModule { }
